// ============================================================
// EmployeePage.tsx — صفحة الموظف الكاملة
// مُناوِب v2 — تطابق كامل مع النظام الأصلي
// ============================================================

import { useState, useEffect, useCallback } from 'react';
import { useAuthStore, useIncidents, useCreateIncident, useIncident, useIncidentAction, useSlaStatus } from '@munawb/hooks';
import { useNotifications, useMarkNotificationsRead } from '@munawb/hooks';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createIncidentSchema, type CreateIncidentInput } from '@munawb/validators';
import { supabase } from '@munawb/supabase';
import type { IncidentRow } from '@munawb/types';
import { useQuery } from '@tanstack/react-query';
import { RISK_LEVEL_COLORS, formatDate, getInitials } from '../lib/utils';

/* ── Types ───────────────────────────────────────────────────── */
type TabId = 'my-incidents' | 'assigned' | 'new-incident';
type Department   = { id: string; name: string };
type MainCategory = { name: string; subs: string[] };
interface Toast { id: number; msg: string; type: 'success' | 'error' | 'info' }

/* ── Constants ───────────────────────────────────────────────── */
const PAGE_SIZE = 12;

const STATUS_STYLE: Record<string, { bg: string; color: string; border: string }> = {
  'مفتوح':         { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  'مصعد':          { bg: '#fff7ed', color: '#ea580c', border: '#fed7aa' },
  'موجه':          { bg: '#f0f9ff', color: '#0284c7', border: '#bae6fd' },
  'طلب RCA':       { bg: '#faf5ff', color: '#9333ea', border: '#e9d5ff' },
  'مغلق':          { bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' },
  'RCA Completed': { bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' },
};
const RISK_BORDER: Record<string, string> = {
  'منخفض': '#10b981', 'متوسط': '#f59e0b',
  'عالي':  '#ea580c', 'مرتفع': '#ea580c', 'حرج': '#ef4444',
};
const RISK_GRADIENT: Record<string, string> = {
  'منخفض': 'linear-gradient(135deg,#10b981,#059669)',
  'متوسط': 'linear-gradient(135deg,#f59e0b,#d97706)',
  'عالي':  'linear-gradient(135deg,#ea580c,#c2410c)',
  'مرتفع': 'linear-gradient(135deg,#ea580c,#c2410c)',
  'حرج':   'linear-gradient(135deg,#ef4444,#b91c1c)',
};
const LOG_ACTION_STYLE: Record<string, { bg: string; color: string; icon: string }> = {
  update:      { bg:'#eff6ff', color:'#2563eb', icon:'🔄' },
  close:       { bg:'#f0fdf4', color:'#16a34a', icon:'✅' },
  escalate:    { bg:'#fff7ed', color:'#ea580c', icon:'🔺' },
  assign:      { bg:'#faf5ff', color:'#9333ea', icon:'👤' },
  direct:      { bg:'#f0f9ff', color:'#0284c7', icon:'➡️' },
  request_rca: { bg:'#fdf4ff', color:'#a21caf', icon:'🔬' },
  rca:         { bg:'#fdf4ff', color:'#a21caf', icon:'📋' },
  reopen:      { bg:'#fefce8', color:'#ca8a04', icon:'🔓' },
};

/* ══════════════════════════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════════════════════════ */
export default function EmployeePage() {
  const { profile }   = useAuthStore();
  const [activeTab, setActiveTab]       = useState<TabId>('my-incidents');
  const [darkMode, setDarkMode]         = useState(() => localStorage.getItem('munawb_theme') === 'dark');
  const [toasts, setToasts]             = useState<Toast[]>([]);
  const [selectedId, setSelectedId]     = useState<string | null>(null);
  const [showNotifModal, setShowNotifModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showCallModal, setShowCallModal]     = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [rcaViewId, setRcaViewId]             = useState<string | null>(null);

  /* ── Search & Filter state ── */
  const [searchMy,      setSearchMy]      = useState('');
  const [statusMy,      setStatusMy]      = useState('');
  const [pageMy,        setPageMy]        = useState(1);
  const [searchAssigned, setSearchAssigned] = useState('');
  const [pageAssigned,   setPageAssigned]   = useState(1);

  /* ── Dark mode ── */
  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('munawb_theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  /* ── Toast helper ── */
  const showToast = useCallback((msg: string, type: Toast['type'] = 'info') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, msg, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
  }, []);

  /* ── Data: my incidents ── */
  const { data: myIncidents = [], isLoading: loadingMy } = useIncidents({
    reporter_id: profile?.id,
    include_closed: true,
  });

  /* ── Data: assigned incidents (same dept, not mine) ── */
  const { data: deptIncidents = [], isLoading: loadingAssigned } = useIncidents({
    dept_id: (profile as any)?.dept_id ?? undefined,
    include_closed: false,
  });
  const assignedIncidents = deptIncidents.filter(i => i.reporter_id !== profile?.id);

  /* ── Data: departments ── */
  const { data: departments = [] } = useQuery<Department[]>({
    queryKey: ['departments'],
    queryFn: async () => {
      const { data } = await supabase.from('departments').select('id,name').eq('active', true).order('name');
      return data ?? [];
    },
  });

  /* ── Data: categories ── */
  const { data: categories = [] } = useQuery<MainCategory[]>({
    queryKey: ['event_categories'],
    queryFn: async () => {
      const { data } = await supabase
        .from('event_categories')
        .select('main_cat,sub_cat')
        .eq('active', true)
        .order('main_cat');
      if (!data) return [];
      const map = new Map<string, string[]>();
      for (const row of data) {
        if (!map.has(row.main_cat)) map.set(row.main_cat, []);
        if (row.sub_cat) map.get(row.main_cat)!.push(row.sub_cat);
      }
      return Array.from(map.entries()).map(([name, subs]) => ({ name, subs }));
    },
  });

  /* ── Notifications ── */
  const { data: notifications = [] } = useNotifications(profile?.id ?? '', null, true);
  const { mutate: markRead }          = useMarkNotificationsRead(profile?.id ?? '');
  const unreadCount = notifications.filter(n => !n.is_read).length;

  /* ── Stats ── */
  const total  = myIncidents.length;
  const open   = myIncidents.filter(i => !['مغلق','RCA Completed'].includes(i.status)).length;
  const closed = myIncidents.filter(i => ['مغلق','RCA Completed'].includes(i.status)).length;

  /* ── Filter & paginate my incidents ── */
  const filteredMy = myIncidents.filter(i => {
    const matchStatus = !statusMy || i.status === statusMy;
    const q = searchMy.toLowerCase();
    const matchSearch = !q || i.description.toLowerCase().includes(q) ||
      i.event_type.toLowerCase().includes(q) || i.location.toLowerCase().includes(q) ||
      i.event_id.toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });
  const totalPagesMy  = Math.max(1, Math.ceil(filteredMy.length / PAGE_SIZE));
  const slicedMy      = filteredMy.slice((pageMy - 1) * PAGE_SIZE, pageMy * PAGE_SIZE);

  /* ── Filter & paginate assigned ── */
  const filteredAssigned = assignedIncidents.filter(i => {
    const q = searchAssigned.toLowerCase();
    return !q || i.description.toLowerCase().includes(q) ||
      i.event_type.toLowerCase().includes(q) || i.location.toLowerCase().includes(q);
  });
  const totalPagesAssigned = Math.max(1, Math.ceil(filteredAssigned.length / PAGE_SIZE));
  const slicedAssigned     = filteredAssigned.slice((pageAssigned - 1) * PAGE_SIZE, pageAssigned * PAGE_SIZE);

  const handleMarkAllRead = () => {
    const ids = notifications.filter(n => !n.is_read).map(n => n.id);
    if (ids.length) markRead(ids);
  };

  const handleSignOut = async () => {
    setShowLogoutModal(false);
    localStorage.removeItem('munawb-profile');
    await supabase.auth.signOut();
  };

  /* ── Render ── */
  return (
    <div dir="rtl" style={{ minHeight:'100vh', fontFamily:"'Tajawal',sans-serif", background: darkMode ? '#020617' : '#f1f5f9', color: darkMode ? '#f1f5f9' : '#0f172a' }}>

      {/* ── Toasts ── */}
      <div style={{ position:'fixed', bottom:24, left:'50%', transform:'translateX(-50%)', zIndex:9999, display:'flex', flexDirection:'column', gap:8, alignItems:'center' }}>
        {toasts.map(t => (
          <div key={t.id} style={{
            background: t.type==='success'?'#059669':t.type==='error'?'#dc2626':'#2563eb',
            color:'#fff', borderRadius:12, padding:'10px 20px',
            fontWeight:800, fontSize:13, boxShadow:'0 8px 24px rgba(0,0,0,0.2)',
            animation:'fadeInUp 0.3s ease',
          }}>{t.msg}</div>
        ))}
      </div>

      {/* ════════════════════════════════════════════════
          HEADER
      ════════════════════════════════════════════════ */}
      <header style={{
        background:'linear-gradient(135deg,#0f172a,#1e293b)',
        position:'sticky', top:0, zIndex:40,
        boxShadow:'0 4px 24px rgba(0,0,0,0.3)',
      }}>
        <div style={{ maxWidth:1200, margin:'0 auto', padding:'0 20px', height:64, display:'flex', alignItems:'center', justifyContent:'space-between' }}>

          {/* Logo */}
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{
              width:38, height:38, background:'linear-gradient(135deg,#3b82f6,#2563eb)',
              borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center',
              fontWeight:900, color:'#fff', fontSize:16, boxShadow:'0 2px 10px rgba(59,130,246,0.4)',
            }}>م</div>
            <div>
              <div style={{ color:'#fff', fontWeight:900, fontSize:17 }}>مُناوِب</div>
              <div style={{ color:'#94a3b8', fontSize:10, fontWeight:700 }}>بوابة الموظف التشغيلية</div>
            </div>
          </div>

          {/* Right controls */}
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>

            {/* User info */}
            <div style={{ display:'flex', alignItems:'center', gap:8, marginLeft:4 }}>
              <div style={{
                width:34, height:34, background:'linear-gradient(135deg,#3b82f6,#1d4ed8)',
                borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center',
                color:'#fff', fontSize:13, fontWeight:900,
              }}>{getInitials(profile?.name ?? 'م')}</div>
              <div>
                <div style={{ color:'#fff', fontSize:13, fontWeight:800 }}>{profile?.name}</div>
                <div style={{ color:'#94a3b8', fontSize:10, fontWeight:600 }}>
                  {(profile as any)?.department?.name ?? 'موظف'}
                </div>
              </div>
            </div>

            {/* Call button */}
            <HeaderBtn onClick={() => setShowCallModal(true)} title="نظام الاستدعاء الطارئ">
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="#fb923c" strokeWidth={2.2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"/>
              </svg>
            </HeaderBtn>

            {/* Profile button */}
            <HeaderBtn onClick={() => setShowProfileModal(true)} title="الملف الشخصي">
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
              </svg>
            </HeaderBtn>

            {/* Notification bell */}
            <HeaderBtn onClick={() => setShowNotifModal(true)} title="الإشعارات">
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
              </svg>
              {unreadCount > 0 && (
                <span style={{
                  position:'absolute', top:5, right:5,
                  width:16, height:16, background:'#ef4444',
                  borderRadius:'50%', fontSize:9, fontWeight:900, color:'#fff',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  border:'2px solid #0f172a',
                }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
              )}
            </HeaderBtn>

            {/* Dark mode */}
            <HeaderBtn onClick={() => setDarkMode(d => !d)} title="الوضع الليلي">
              <span style={{ fontSize:16 }}>{darkMode ? '☀️' : '🌙'}</span>
            </HeaderBtn>

            {/* Sign out */}
            <button onClick={() => setShowLogoutModal(true)} style={{
              background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.3)',
              borderRadius:10, padding:'7px 14px', color:'#fca5a5',
              fontSize:12, fontWeight:800, cursor:'pointer', fontFamily:"'Tajawal',sans-serif",
            }}>خروج</button>
          </div>
        </div>

        {/* Tabs bar */}
        <div style={{ maxWidth:1200, margin:'0 auto', padding:'0 20px', display:'flex', gap:0, overflowX:'auto' }}>
          {([
            { id:'my-incidents', label:'بلاغاتي السابقة',  icon:'📋', badge: total > 0 ? total : null },
            { id:'assigned',     label:'مهام أُسندت إلي',  icon:'📎', badge: assignedIncidents.length > 0 ? assignedIncidents.length : null },
            { id:'new-incident', label:'تسجيل حدث جديد',   icon:'➕', badge: null },
          ] as { id: TabId; label: string; icon: string; badge: number | null }[]).map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              padding:'10px 20px', border:'none', cursor:'pointer',
              fontFamily:"'Tajawal',sans-serif", fontWeight:800, fontSize:13,
              display:'flex', alignItems:'center', gap:6,
              background:'transparent', whiteSpace:'nowrap',
              color: activeTab === tab.id ? '#60a5fa' : '#94a3b8',
              borderBottom: activeTab === tab.id ? '3px solid #60a5fa' : '3px solid transparent',
              transition:'all 0.2s',
            }}>
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
              {tab.badge !== null && (
                <span style={{
                  background: activeTab === tab.id ? '#3b82f6' : 'rgba(255,255,255,0.1)',
                  color:'#fff', borderRadius:20, padding:'1px 7px', fontSize:10, fontWeight:900,
                }}>{tab.badge}</span>
              )}
            </button>
          ))}
        </div>
      </header>

      {/* ════════════════════════════════════════════════
          MAIN
      ════════════════════════════════════════════════ */}
      <main style={{ maxWidth:1200, margin:'0 auto', padding:'24px 20px 60px' }}>

        {/* Welcome card */}
        <WelcomeCard profile={profile} total={total} open={open} closed={closed} />

        {/* ── TAB: MY INCIDENTS ── */}
        {activeTab === 'my-incidents' && (
          <div>
            {/* Search & filter bar */}
            <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginBottom:20, alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', flex:1 }}>
                <div style={{ position:'relative' }}>
                  <span style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', fontSize:14, color:'#94a3b8' }}>🔍</span>
                  <input
                    type="search" placeholder="بحث..." value={searchMy}
                    onChange={e => { setSearchMy(e.target.value); setPageMy(1); }}
                    style={{ ...inputStyle(darkMode), paddingRight:32, width:180 }}
                  />
                </div>
                <select value={statusMy} onChange={e => { setStatusMy(e.target.value); setPageMy(1); }}
                  style={inputStyle(darkMode)}>
                  <option value="">جميع الحالات</option>
                  {['مفتوح','مصعد','موجه','طلب RCA','مغلق','RCA Completed'].map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <span style={{ color:'#94a3b8', fontSize:12, fontWeight:700, alignSelf:'center' }}>
                  {filteredMy.length} نتيجة
                </span>
              </div>
              <button onClick={() => window.location.reload()} style={{ ...btnSecondary(darkMode) }}>
                🔄 تحديث
              </button>
            </div>

            {loadingMy ? <SkeletonGrid /> : filteredMy.length === 0 ? (
              <EmptyState onNew={() => setActiveTab('new-incident')} />
            ) : (
              <>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:16, marginBottom:20 }}>
                  {slicedMy.map(inc => (
                    <IncidentCard key={inc.id} incident={inc} darkMode={darkMode} onClick={() => setSelectedId(inc.id)} onRcaView={setRcaViewId} />
                  ))}
                </div>
                <Pagination page={pageMy} total={totalPagesMy} onChange={setPageMy} darkMode={darkMode} />
              </>
            )}
          </div>
        )}

        {/* ── TAB: ASSIGNED ── */}
        {activeTab === 'assigned' && (
          <div>
            <div style={{ display:'flex', gap:8, marginBottom:20, alignItems:'center' }}>
              <div style={{ position:'relative' }}>
                <span style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', fontSize:14, color:'#94a3b8' }}>🔍</span>
                <input
                  type="search" placeholder="بحث..." value={searchAssigned}
                  onChange={e => { setSearchAssigned(e.target.value); setPageAssigned(1); }}
                  style={{ ...inputStyle(darkMode), paddingRight:32, width:180 }}
                />
              </div>
              <span style={{ color:'#94a3b8', fontSize:12, fontWeight:700 }}>{filteredAssigned.length} نتيجة</span>
            </div>

            {loadingAssigned ? <SkeletonGrid /> : filteredAssigned.length === 0 ? (
              <div style={{ ...emptyBox(darkMode), borderColor:'#10b981' }}>
                <div style={{ fontSize:48, marginBottom:12 }}>☕</div>
                <div style={{ color:'#10b981', fontSize:16, fontWeight:900 }}>لا توجد مهام مسندة إليك حالياً</div>
              </div>
            ) : (
              <>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:16, marginBottom:20 }}>
                  {slicedAssigned.map(inc => (
                    <IncidentCard key={inc.id} incident={inc} darkMode={darkMode} onClick={() => setSelectedId(inc.id)} assigned />
                  ))}
                </div>
                <Pagination page={pageAssigned} total={totalPagesAssigned} onChange={setPageAssigned} darkMode={darkMode} />
              </>
            )}
          </div>
        )}

        {/* ── TAB: NEW INCIDENT FORM ── */}
        {activeTab === 'new-incident' && (
          <NewIncidentForm
            departments={departments}
            categories={categories}
            profile={profile}
            onSuccess={() => { showToast('تم إرسال الحدث بنجاح ✅', 'success'); setActiveTab('my-incidents'); }}
            onError={(msg) => showToast(msg, 'error')}
            darkMode={darkMode}
          />
        )}

      </main>

      {/* ════════════════════════════════════════════════
          DETAILS MODAL
      ════════════════════════════════════════════════ */}
      {selectedId && (
        <DetailsModal
          id={selectedId}
          profile={profile}
          darkMode={darkMode}
          onClose={() => setSelectedId(null)}
          onSuccess={(msg) => { showToast(msg, 'success'); setSelectedId(null); }}
          onError={(msg) => showToast(msg, 'error')}
        />
      )}

      {/* ════════════════════════════════════════════════
          NOTIFICATIONS MODAL
      ════════════════════════════════════════════════ */}
      {showNotifModal && (
        <NotifModal
          notifications={notifications}
          unreadCount={unreadCount}
          darkMode={darkMode}
          onClose={() => setShowNotifModal(false)}
          onMarkAll={handleMarkAllRead}
          onClickNotif={(notifId, incidentId) => {
            const ids = [notifId];
            markRead(ids);
            setShowNotifModal(false);
            if (incidentId) setSelectedId(incidentId);
          }}
        />
      )}

      {/* ════════════════════════════════════════════════
          PROFILE MODAL
      ════════════════════════════════════════════════ */}
      {showProfileModal && (
        <ProfileModal
          profile={profile}
          darkMode={darkMode}
          onClose={() => setShowProfileModal(false)}
          onSuccess={(msg) => { showToast(msg, 'success'); setShowProfileModal(false); }}
          onError={(msg) => showToast(msg, 'error')}
        />
      )}

      {/* ════════════════════════════════════════════════
          CALL MODAL
      ════════════════════════════════════════════════ */}
      {showCallModal && (
        <CallModal
          profile={profile}
          departments={departments}
          darkMode={darkMode}
          onClose={() => setShowCallModal(false)}
          onSuccess={(msg) => showToast(msg, 'success')}
          onError={(msg) => showToast(msg, 'error')}
        />
      )}

      {/* ════════════════════════════════════════════════
          RCA VIEW MODAL
      ════════════════════════════════════════════════ */}
      {rcaViewId && (
        <RcaViewModal
          incidentId={rcaViewId}
          darkMode={darkMode}
          onClose={() => setRcaViewId(null)}
        />
      )}

      {/* ════════════════════════════════════════════════
          LOGOUT MODAL
      ════════════════════════════════════════════════ */}
      {showLogoutModal && (
        <LogoutModal
          darkMode={darkMode}
          onConfirm={handleSignOut}
          onCancel={() => setShowLogoutModal(false)}
        />
      )}

      <style>{`
        @keyframes fadeInUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
        @keyframes modalIn  { from{opacity:0;transform:scale(0.95)} to{opacity:1;transform:scale(1)} }
        @keyframes emergencyPulse { 0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,0.4)} 50%{box-shadow:0 0 0 14px rgba(239,68,68,0)} }
      `}</style>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   WELCOME CARD
══════════════════════════════════════════════════════════════ */
function WelcomeCard({ profile, total, open, closed }: { profile: any; total: number; open: number; closed: number }) {
  return (
    <div style={{
      background:'linear-gradient(135deg,#1f3b73,#2563eb)',
      borderRadius:20, padding:'24px 28px', marginBottom:24,
      position:'relative', overflow:'hidden',
      boxShadow:'0 10px 30px rgba(37,99,235,0.3)',
    }}>
      <div style={{ position:'absolute', top:-40, left:-40, width:180, height:180, background:'rgba(255,255,255,0.05)', borderRadius:'50%' }} />
      <div style={{ position:'relative', zIndex:1 }}>
        <div style={{ color:'rgba(255,255,255,0.7)', fontSize:13, fontWeight:700 }}>أهلاً،</div>
        <div style={{ color:'#fff', fontSize:21, fontWeight:900, marginBottom:6 }}>{profile?.name} 👋</div>
        {profile?.department?.name && (
          <div style={{ display:'inline-flex', alignItems:'center', gap:6, background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:20, padding:'3px 12px', color:'rgba(255,255,255,0.9)', fontSize:12, fontWeight:700, marginBottom:16 }}>
            🏥 {profile.department.name}
          </div>
        )}
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginTop:16 }}>
          {[
            { label:'إجمالي البلاغات', value: total,  icon:'📋', acc:'rgba(255,255,255,0.2)' },
            { label:'مفتوحة',           value: open,   icon:'🔓', acc:'rgba(251,191,36,0.3)'  },
            { label:'مغلقة',            value: closed, icon:'✅', acc:'rgba(52,211,153,0.3)'  },
          ].map(s => (
            <div key={s.label} style={{ background:s.acc, border:'1px solid rgba(255,255,255,0.15)', borderRadius:14, padding:'10px 16px', display:'flex', alignItems:'center', gap:8, flex:'1 1 100px', minWidth:90 }}>
              <span style={{ fontSize:20 }}>{s.icon}</span>
              <div>
                <div style={{ color:'#fff', fontSize:20, fontWeight:900, lineHeight:1 }}>{s.value}</div>
                <div style={{ color:'rgba(255,255,255,0.7)', fontSize:10, fontWeight:700 }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   INCIDENT CARD
══════════════════════════════════════════════════════════════ */
function IncidentCard({ incident, darkMode, onClick, assigned, onRcaView }: {
  incident: IncidentRow; darkMode: boolean; onClick: () => void; assigned?: boolean;
  onRcaView?: (id: string) => void;
}) {
  const statusStyle  = STATUS_STYLE[incident.status] ?? STATUS_STYLE['مفتوح'];
  const borderColor  = assigned ? '#10b981' : (RISK_BORDER[incident.risk_level] ?? '#3b82f6');
  const sla          = useSlaStatus(incident.created_at, incident.sla_minutes, incident.closed_at ?? null, incident.status);

  return (
    <div
      onClick={onClick}
      style={{
        background: darkMode ? 'rgba(15,23,42,0.85)' : '#fff',
        borderRadius:20, padding:'18px 20px',
        boxShadow:'0 4px 16px rgba(0,0,0,0.06)',
        border:`1px solid ${darkMode ? '#1e293b' : '#e2e8f0'}`,
        borderRight:`4px solid ${borderColor}`,
        cursor:'pointer', transition:'all 0.2s',
      }}
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform='translateY(-3px)'; (e.currentTarget as HTMLElement).style.boxShadow='0 12px 28px rgba(0,0,0,0.12)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform=''; (e.currentTarget as HTMLElement).style.boxShadow='0 4px 16px rgba(0,0,0,0.06)'; }}
    >
      {/* Top row */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
        <span style={{
          background: darkMode ? '#1e293b' : '#f1f5f9',
          color: darkMode ? '#94a3b8' : '#64748b',
          fontFamily:'monospace', fontSize:11, fontWeight:800,
          padding:'2px 8px', borderRadius:6,
        }}>{incident.event_id}</span>
        <div style={{ display:'flex', flexDirection:'column', gap:4, alignItems:'flex-end' }}>
          <span style={{
            background: statusStyle.bg, color: statusStyle.color,
            border:`1px solid ${statusStyle.border}`,
            borderRadius:20, padding:'3px 10px', fontSize:10, fontWeight:800,
          }}>{incident.status}</span>
          {assigned && (
            <span style={{ background:'#ecfdf5', color:'#059669', border:'1px solid #bbf7d0', borderRadius:20, padding:'2px 8px', fontSize:10, fontWeight:800 }}>
              تكليف
            </span>
          )}
        </div>
      </div>

      {/* Description */}
      <p style={{
        color: darkMode ? '#e2e8f0' : '#1e293b', fontSize:13, fontWeight:800,
        lineHeight:1.5, marginBottom:6,
        display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden',
      }}>{incident.description}</p>

      {/* Location + risk */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
        <span style={{ color:'#94a3b8', fontSize:11, fontWeight:600 }}>📍 {incident.location}</span>
        <span style={{
          background: RISK_GRADIENT[incident.risk_level] ?? RISK_GRADIENT['منخفض'],
          color:'#fff', borderRadius:20, padding:'2px 10px', fontSize:10, fontWeight:900,
        }}>{incident.risk_level}</span>
      </div>

      {/* SLA bar */}
      <SlaBar sla={sla} status={incident.status} darkMode={darkMode} />

      {/* Date */}
      <div style={{ color:'#94a3b8', fontSize:10, fontWeight:600, marginTop:8, textAlign:'left' }} dir="ltr">
        {new Date(incident.created_at).toLocaleString('en-GB', { dateStyle:'short', timeStyle:'short' })}
      </div>

      {/* RCA View Button — shown only when status is RCA Completed */}
      {incident.status === 'RCA Completed' && onRcaView && (
        <button
          onClick={e => { e.stopPropagation(); onRcaView(incident.id); }}
          style={{
            marginTop:10, width:'100%', padding:'8px 0',
            background:'linear-gradient(135deg,#6d28d9,#4c1d95)',
            border:'none', borderRadius:10, color:'#fff',
            fontSize:12, fontWeight:900, fontFamily:"'Tajawal',sans-serif",
            cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6,
          }}
        >
          📋 عرض تقرير تحليل السبب الجذري
        </button>
      )}
    </div>
  );
}

/* ── SLA Bar ── */
function SlaBar({ sla, status, darkMode }: { sla: ReturnType<typeof useSlaStatus>; status: string; darkMode: boolean }) {
  if (status === 'مغلق' || status === 'RCA Completed') {
    return (
      <div style={{ background: darkMode ? '#1e293b' : '#f1f5f9', borderRadius:8, padding:'6px 10px', textAlign:'center' }}>
        <span style={{ color:'#64748b', fontSize:10, fontWeight:800 }}>✅ تم الإغلاق نهائياً</span>
      </div>
    );
  }
  const barColor = sla.isBreached ? '#ef4444' : sla.pct > 80 ? '#f59e0b' : '#10b981';
  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
        <span style={{ fontSize:10, fontWeight:800, color: sla.isBreached ? '#ef4444' : '#64748b' }}>
          {sla.isBreached ? '⚠️ تجاوز SLA' : `⏱ متبقي: ${sla.formatRemaining()}`}
        </span>
        <span style={{ fontSize:9, color:'#94a3b8', fontWeight:700 }}>{sla.pct}%</span>
      </div>
      <div style={{ background: darkMode ? '#1e293b' : '#e2e8f0', borderRadius:99, height:5, overflow:'hidden' }}>
        <div style={{ width:`${sla.pct}%`, height:'100%', background:barColor, borderRadius:99, transition:'width 0.5s' }} />
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   DETAILS MODAL
══════════════════════════════════════════════════════════════ */
function DetailsModal({ id, profile, darkMode, onClose, onSuccess, onError }: {
  id: string; profile: any; darkMode: boolean;
  onClose: () => void; onSuccess: (msg: string) => void; onError: (msg: string) => void;
}) {
  const { data: incident, isLoading } = useIncident(id);
  const { mutateAsync: doAction, isPending } = useIncidentAction();
  const [updateDesc, setUpdateDesc] = useState('');

  const bg    = darkMode ? '#0f172a' : '#fff';
  const bgSub = darkMode ? '#1e293b' : '#f8fafc';

  const handleAction = async (action: 'update' | 'close') => {
    if (!updateDesc.trim()) { onError('يرجى كتابة تفاصيل الإجراء'); return; }
    try {
      await doAction({ incident_id: id, action_type: action, description: updateDesc });
      onSuccess(action === 'close' ? 'تم إغلاق الحدث ✅' : 'تم تحديث الإجراء ✅');
    } catch (e: any) { onError(e?.message ?? 'حدث خطأ'); }
  };

  const isLocked = incident && ['مغلق','RCA Completed','مصعد'].includes(incident.status);

  return (
    <div onClick={onClose} style={{
      position:'fixed', inset:0, zIndex:60,
      background:'rgba(15,23,42,0.75)', backdropFilter:'blur(6px)',
      display:'flex', justifyContent:'center', alignItems:'center', padding:16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background:bg, width:'100%', maxWidth:680, borderRadius:24,
        overflow:'hidden', display:'flex', flexDirection:'column',
        maxHeight:'88vh', animation:'modalIn 0.25s ease',
        border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
        boxShadow:'0 24px 64px rgba(0,0,0,0.3)',
      }}>
        {/* Modal header */}
        <div style={{ background:'linear-gradient(135deg,#0f172a,#1e293b)', padding:'16px 20px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ color:'#fff', fontWeight:900, fontSize:15, display:'flex', alignItems:'center', gap:8 }}>
            📄 تفاصيل البلاغ
            {incident && <span style={{ background:'#1e293b', color:'#94a3b8', fontFamily:'monospace', fontSize:11, padding:'2px 8px', borderRadius:6 }}>{incident.event_id}</span>}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.7)', fontSize:20, cursor:'pointer' }}>✕</button>
        </div>

        {isLoading ? (
          <div style={{ padding:60, textAlign:'center', color:'#94a3b8' }}>
            <div style={{ fontSize:32 }}>⏳</div>
            <div style={{ fontWeight:700, marginTop:8 }}>جارٍ التحميل...</div>
          </div>
        ) : incident ? (
          <div style={{ overflowY:'auto', flex:1, padding:'20px' }}>

            {/* Status + time */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <div>
                <div style={{ color:'#94a3b8', fontSize:10, fontWeight:700, marginBottom:4 }}>وقت التسجيل</div>
                <div style={{ fontWeight:800, fontSize:13 }} dir="ltr">
                  {new Date(incident.created_at).toLocaleString('en-GB', { dateStyle:'medium', timeStyle:'short' })}
                </div>
              </div>
              <div style={{ textAlign:'left' }}>
                <div style={{ color:'#94a3b8', fontSize:10, fontWeight:700, marginBottom:4 }}>الحالة</div>
                {(() => { const s = STATUS_STYLE[incident.status] ?? STATUS_STYLE['مفتوح']; return (
                  <span style={{ background:s.bg, color:s.color, border:`1px solid ${s.border}`, borderRadius:20, padding:'4px 14px', fontSize:12, fontWeight:900 }}>
                    {incident.status}
                  </span>
                ); })()}
              </div>
            </div>

            {/* Description */}
            <div style={{ background:bgSub, borderRadius:14, padding:16, marginBottom:14, border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`, position:'relative' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                <div style={{ color:'#94a3b8', fontSize:10, fontWeight:700 }}>الوصف الكامل</div>
                <button
                  onClick={() => shareIncidentWhatsApp(incident)}
                  title="مشاركة عبر واتساب"
                  style={{ background:'none', border:'none', cursor:'pointer', color:'#25d366', fontSize:20, lineHeight:1, padding:0 }}
                >
                  {waIcon}
                </button>
              </div>
              <p style={{ fontWeight:700, fontSize:13, lineHeight:1.7, margin:0 }}>{incident.description}</p>
            </div>

            {/* Details grid */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 }}>
              {[
                { label:'الموقع / القسم', icon:'📍', value: incident.location },
                { label:'نوع الحدث',      icon:'⚡', value: incident.event_type },
                { label:'مستوى الخطورة',  icon:'⚠️', value: incident.risk_level },
                { label:'درجة الخطورة',   icon:'🔢', value: String(incident.risk_score) },
              ].map(d => (
                <div key={d.label} style={{ background:bgSub, borderRadius:12, padding:'12px 14px', border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}` }}>
                  <div style={{ color:'#94a3b8', fontSize:10, fontWeight:700, marginBottom:4 }}>{d.icon} {d.label}</div>
                  <div style={{ fontWeight:900, fontSize:13 }}>{d.value}</div>
                </div>
              ))}
            </div>

            {/* Logs */}
            {(incident as any).logs && (incident as any).logs.length > 0 && (
              <div style={{ marginBottom:14 }}>
                <div style={{ color: darkMode ? '#e2e8f0' : '#1e293b', fontWeight:900, fontSize:13, marginBottom:10, display:'flex', alignItems:'center', gap:6 }}>
                  📜 سجل الإجراءات والتحديثات
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {(incident as any).logs.map((log: any) => {
                    const s = LOG_ACTION_STYLE[log.action_type] ?? LOG_ACTION_STYLE['update'];
                    return (
                      <div key={log.id} style={{ background:s.bg, borderRadius:12, padding:'10px 14px', border:`1px solid rgba(0,0,0,0.05)` }}>
                        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                          <span style={{ color:s.color, fontSize:12, fontWeight:800, display:'flex', alignItems:'center', gap:5 }}>
                            {s.icon} {log.action_name ?? log.action_type}
                          </span>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            {log.description && (
                              <button
                                onClick={() => shareUpdateWhatsApp(incident.event_id, log.description, log.actor_name ?? 'النظام')}
                                title="مشاركة هذا التحديث عبر واتساب"
                                style={{ background:'none', border:'none', cursor:'pointer', color:'#25d366', fontSize:16, lineHeight:1, padding:0 }}
                              >
                                {waIcon}
                              </button>
                            )}
                            <span style={{ color:'#94a3b8', fontSize:10, fontWeight:600 }} dir="ltr">
                              {new Date(log.created_at).toLocaleString('en-GB', { dateStyle:'short', timeStyle:'short' })}
                            </span>
                          </div>
                        </div>
                        {log.description && <p style={{ color:'#374151', fontSize:12, fontWeight:600, margin:0, lineHeight:1.5 }}>{log.description}</p>}
                        <div style={{ color:'#94a3b8', fontSize:10, fontWeight:700, marginTop:4 }}>
                          بواسطة: {log.actor_name ?? 'النظام'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Action area */}
            {!isLocked ? (
              <div style={{ background:'#eff6ff', borderRadius:14, padding:16, border:'1px solid #bfdbfe' }}>
                <div style={{ color:'#1d4ed8', fontWeight:900, fontSize:13, marginBottom:10 }}>
                  ✏️ إضافة تحديث للإجراء المُتخذ
                </div>
                <textarea
                  rows={3}
                  value={updateDesc}
                  onChange={e => setUpdateDesc(e.target.value)}
                  placeholder="اكتب تفاصيل ما قمت به لحل المشكلة..."
                  style={{ width:'100%', borderRadius:10, border:'1px solid #bfdbfe', padding:'10px 12px', fontSize:13, fontWeight:600, fontFamily:"'Tajawal',sans-serif", resize:'none', outline:'none', background:'#fff', color:'#1e293b', boxSizing:'border-box' }}
                />
                <div style={{ display:'flex', gap:10, marginTop:10 }}>
                  <button
                    onClick={() => handleAction('update')}
                    disabled={isPending}
                    style={{ flex:1, background:'#2563eb', border:'none', borderRadius:10, padding:'12px', color:'#fff', fontWeight:900, fontSize:13, cursor:'pointer', fontFamily:"'Tajawal',sans-serif" }}
                  >
                    {isPending ? '...' : '🔄 تحديث الإجراء'}
                  </button>
                  <button
                    onClick={() => handleAction('close')}
                    disabled={isPending}
                    style={{ background:'#dc2626', border:'none', borderRadius:10, padding:'12px 20px', color:'#fff', fontWeight:900, fontSize:13, cursor:'pointer', fontFamily:"'Tajawal',sans-serif" }}
                  >
                    ✅ إغلاق الحدث
                  </button>
                </div>
              </div>
            ) : incident.status === 'مغلق' || incident.status === 'RCA Completed' ? (
              <div style={{ background:'#f8fafc', borderRadius:14, padding:16, border:'1px solid #e2e8f0', textAlign:'center' }}>
                <div style={{ fontSize:32 }}>✅</div>
                <div style={{ color:'#64748b', fontWeight:700, fontSize:13, marginTop:8 }}>هذا الحدث مغلق نهائياً.</div>
              </div>
            ) : (
              <div style={{ background:'#fff7ed', borderRadius:14, padding:16, border:'1px solid #fed7aa', textAlign:'center' }}>
                <div style={{ fontSize:32 }}>🔒</div>
                <div style={{ color:'#ea580c', fontWeight:800, fontSize:13, marginTop:8 }}>
                  هذا الحدث "مُصعد" حالياً.<br/>
                  <span style={{ fontWeight:600, fontSize:12 }}>لا يمكنك التحديث عليه حتى يتم الرد من الإدارة.</span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ padding:40, textAlign:'center', color:'#ef4444', fontWeight:800 }}>تعذّر تحميل البيانات</div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   NOTIFICATIONS MODAL
══════════════════════════════════════════════════════════════ */
const NOTIF_META: Record<string, { icon: string; color: string }> = {
  new_event:    { icon:'⚡', color:'#2563eb' },
  updated:      { icon:'🔄', color:'#0284c7' },
  escalated:    { icon:'🔺', color:'#ea580c' },
  escalation:   { icon:'🔺', color:'#ea580c' },
  rca_request:  { icon:'🔬', color:'#9333ea' },
  rca_complete: { icon:'✅', color:'#9333ea' },
  system:       { icon:'ℹ️',  color:'#64748b' },
};

function NotifModal({ notifications, unreadCount, darkMode, onClose, onMarkAll, onClickNotif }: {
  notifications: any[]; unreadCount: number; darkMode: boolean;
  onClose: () => void; onMarkAll: () => void;
  onClickNotif: (notifId: string, incidentId: string | null) => void;
}) {
  const bg = darkMode ? '#0f172a' : '#fff';
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:70, background:'rgba(15,23,42,0.6)', backdropFilter:'blur(4px)', display:'flex', justifyContent:'center', alignItems:'center', padding:16 }}>
      <div onClick={e => e.stopPropagation()} style={{
        background:bg, width:'100%', maxWidth:460, borderRadius:24,
        overflow:'hidden', display:'flex', flexDirection:'column',
        maxHeight:'80vh', animation:'modalIn 0.2s ease',
        border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
        boxShadow:'0 24px 64px rgba(0,0,0,0.25)',
      }}>
        {/* Header */}
        <div style={{ background:'linear-gradient(135deg,#2563eb,#4f46e5)', padding:'14px 18px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ color:'#fff', fontWeight:900, fontSize:15, display:'flex', alignItems:'center', gap:8 }}>
            🔔 مركز الإشعارات
            {unreadCount > 0 && <span style={{ background:'#ef4444', borderRadius:20, padding:'1px 8px', fontSize:11, fontWeight:900 }}>{unreadCount} جديد</span>}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.8)', fontSize:18, cursor:'pointer' }}>✕</button>
        </div>

        {/* Sub-header */}
        <div style={{ padding:'8px 16px', borderBottom:`1px solid ${darkMode ? '#1e293b' : '#f1f5f9'}`, display:'flex', justifyContent:'space-between', alignItems:'center', background: darkMode ? '#1e293b' : '#f8fafc' }}>
          <span style={{ color:'#94a3b8', fontSize:11, fontWeight:700 }}>الإشعارات الحديثة</span>
          {unreadCount > 0 && (
            <button onClick={onMarkAll} style={{ background:'none', border:'none', color:'#3b82f6', fontSize:11, fontWeight:800, cursor:'pointer', fontFamily:"'Tajawal',sans-serif" }}>
              تحديد الكل كمقروء ✓
            </button>
          )}
        </div>

        {/* List */}
        <div style={{ overflowY:'auto', flex:1 }}>
          {notifications.length === 0 ? (
            <div style={{ padding:40, textAlign:'center' }}>
              <div style={{ fontSize:40 }}>🔕</div>
              <div style={{ color:'#94a3b8', fontWeight:700, fontSize:13, marginTop:8 }}>لا توجد إشعارات</div>
            </div>
          ) : notifications.map((n, idx) => {
            const meta = NOTIF_META[n.type] ?? NOTIF_META['system'];
            return (
              <div
                key={n.id}
                onClick={() => onClickNotif(n.id, n.incident_id ?? null)}
                style={{
                  display:'flex', alignItems:'flex-start', gap:12,
                  padding:'14px 16px',
                  borderBottom: idx < notifications.length - 1 ? `1px solid ${darkMode ? '#1e293b' : '#f8fafc'}` : 'none',
                  background: n.is_read ? (darkMode ? '#0f172a' : '#fff') : (darkMode ? '#1e3a5f' : '#eff6ff'),
                  cursor:'pointer', transition:'background 0.15s',
                }}
              >
                <div style={{ width:36, height:36, background: n.is_read ? (darkMode ? '#1e293b' : '#f1f5f9') : '#dbeafe', borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, flexShrink:0 }}>
                  {meta.icon}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ color: darkMode ? '#e2e8f0' : '#1e293b', fontSize:12, fontWeight: n.is_read ? 600 : 800, margin:0, lineHeight:1.5 }}>
                    {n.message}
                  </p>
                  <span style={{ color:'#94a3b8', fontSize:10, fontWeight:600 }}>
                    {new Date(n.created_at).toLocaleString('ar-SA', { dateStyle:'short', timeStyle:'short' })}
                  </span>
                </div>
                {!n.is_read && <div style={{ width:8, height:8, background:'#3b82f6', borderRadius:'50%', flexShrink:0, marginTop:4 }} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   NEW INCIDENT FORM
══════════════════════════════════════════════════════════════ */
function NewIncidentForm({ departments, categories, profile, onSuccess, onError, darkMode }: {
  departments: Department[];
  categories: MainCategory[];
  profile: any;
  onSuccess: () => void;
  onError: (msg: string) => void;
  darkMode: boolean;
}) {
  const { mutateAsync: createIncident } = useCreateIncident();
  const { register, handleSubmit, reset, watch, setValue, formState: { errors, isSubmitting } } =
    useForm<CreateIncidentInput>({ resolver: zodResolver(createIncidentSchema), defaultValues: { likelihood:1, impact:1, is_repeated:false } });

  const likelihood = watch('likelihood', 1);
  const impact     = watch('impact', 1);
  const riskScore  = likelihood * impact;
  const riskLevel  = computeRiskLevel(riskScore);

  const selectedMain = watch('event_type', '');
  const mainCatForSubs = categories.find(c => c.name === selectedMain);

  const bg    = darkMode ? 'rgba(15,23,42,0.9)' : '#fff';
  const label = (t: string, req?: boolean) => (
    <label style={{ display:'block', marginBottom:6, color:'#64748b', fontSize:12, fontWeight:800 }}>
      {t} {req && <span style={{ color:'#ef4444' }}>*</span>}
    </label>
  );

  const [waPromptData, setWaPromptData] = useState<{ eventId: string; desc: string } | null>(null);

  const onSubmit = async (data: CreateIncidentInput) => {
    try {
      const result = await createIncident({ ...data, reporter_name: profile?.name ?? '' });
      reset();
      onSuccess();
      if ((result as any)?.event_id) {
        setWaPromptData({ eventId: (result as any).event_id, desc: data.description });
      }
    } catch (e: any) { onError(e?.message ?? 'حدث خطأ'); }
  };

  return (
    <div style={{ background:bg, borderRadius:20, padding:'28px 28px', boxShadow:'0 10px 30px rgba(0,0,0,0.06)', border:`1px solid ${darkMode ? '#1e293b' : '#e2e8f0'}`, maxWidth:780, margin:'0 auto' }}>

      {/* WhatsApp prompt after incident creation */}
      {waPromptData && (
        <div style={{
          position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)',
          background:'#1e293b', borderRadius:16, padding:'16px 22px',
          display:'flex', alignItems:'center', gap:12, zIndex:9999,
          boxShadow:'0 16px 40px rgba(0,0,0,0.4)', border:'1px solid rgba(255,255,255,0.1)',
          animation:'modalIn 0.25s ease',
        }}>
          <span style={{ fontSize:20 }}>✅</span>
          <span style={{ color:'#fff', fontWeight:800, fontSize:14 }}>تم التسجيل!</span>
          <button
            onClick={() => {
              const msg = `📋 *بلاغ جديد — ${waPromptData.eventId}*\n━━━━━━━━━━━━━━━━━\n📝 ${waPromptData.desc}\n\n🏥 نظام مُناوِب`;
              window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
              setWaPromptData(null);
            }}
            style={{ background:'#25d366', border:'none', borderRadius:8, padding:'6px 14px', color:'#fff', fontWeight:900, fontSize:12, fontFamily:"'Tajawal',sans-serif", cursor:'pointer', display:'flex', alignItems:'center', gap:5 }}
          >
            {waIcon} شارك
          </button>
          <button
            onClick={() => setWaPromptData(null)}
            style={{ background:'none', border:'none', color:'rgba(255,255,255,0.5)', fontSize:18, cursor:'pointer', lineHeight:1 }}
          >✕</button>
        </div>
      )}
      {/* Form header */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24, paddingBottom:18, borderBottom:`2px solid ${darkMode ? '#1e293b' : '#f1f5f9'}` }}>
        <div style={{ width:44, height:44, background:'linear-gradient(135deg,#3b82f6,#2563eb)', borderRadius:12, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>⚡</div>
        <div>
          <div style={{ color:'#1f3b73', fontSize:17, fontWeight:900 }}>تسجيل حدث تشغيلي جديد</div>
          <div style={{ color:'#64748b', fontSize:12, fontWeight:600, marginTop:2 }}>أدخل تفاصيل الحدث بدقة</div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate style={{ display:'flex', flexDirection:'column', gap:18 }}>

        {/* Row 1: Location + Main Category */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
          <div>
            {label('مكان وقوع الحدث', true)}
            <select className="input-field" {...register('location')}>
              <option value="">اختر القسم...</option>
              {departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
            </select>
            {errors.location && <ErrMsg msg={errors.location.message} />}
          </div>
          <div>
            {label('التصنيف الرئيسي', true)}
            <select className="input-field" {...register('event_type')} onChange={e => { setValue('event_type', e.target.value); }}>
              <option value="">اختر التصنيف...</option>
              {categories.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
            {errors.event_type && <ErrMsg msg={errors.event_type.message} />}
          </div>
        </div>

        {/* Sub category */}
        {mainCatForSubs && mainCatForSubs.subs.length > 0 && (
          <div>
            {label('النوع الفرعي')}
            <select className="input-field" {...register('category_id' as any)}>
              <option value="">بدون تصنيف فرعي</option>
              {mainCatForSubs.subs.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}

        {/* Divider */}
        <div style={{ height:1, background: darkMode ? '#1e293b' : '#f1f5f9' }} />

        {/* Risk section */}
        <div style={{ background: darkMode ? '#1e293b' : '#f8fafc', borderRadius:14, padding:16, border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}` }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:14 }}>
            <div>
              {label('احتمالية التكرار', true)}
              <select className="input-field" {...register('likelihood', { valueAsNumber:true })}>
                <option value={1}>نادر جدًا (1)</option>
                <option value={2}>نادر (2)</option>
                <option value={3}>محتمل (3)</option>
                <option value={4}>مرجّح (4)</option>
                <option value={5}>شبه مؤكد (5)</option>
              </select>
            </div>
            <div>
              {label('الأثر المتوقع', true)}
              <select className="input-field" {...register('impact', { valueAsNumber:true })}>
                <option value={1}>طفيف (1)</option>
                <option value={2}>محدود (2)</option>
                <option value={3}>متوسط (3)</option>
                <option value={4}>جسيم (4)</option>
                <option value={5}>كارثي (5)</option>
              </select>
            </div>
          </div>
          {/* Risk display */}
          <div style={{ background: darkMode ? '#0f172a' : '#fff', borderRadius:10, padding:'12px 16px', border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span style={{ fontWeight:800, fontSize:13 }}>مستوى الخطورة (Risk Level):</span>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ fontWeight:900, fontSize:22, color:'#1f3b73' }}>{riskScore}</span>
              <span style={{ background:RISK_GRADIENT[riskLevel] ?? RISK_GRADIENT['منخفض'], color:'#fff', borderRadius:8, padding:'4px 14px', fontSize:12, fontWeight:900 }}>
                {riskLevel}
              </span>
            </div>
          </div>
        </div>

        {/* Description */}
        <div>
          {label('وصف الحدث بدقة', true)}
          <textarea rows={4} placeholder="اكتب تفاصيل ما حدث..." className="input-field" style={{ resize:'none' }} {...register('description')} />
          {errors.description && <ErrMsg msg={errors.description.message} />}
        </div>

        {/* Bottom row */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, background: darkMode ? '#1e293b' : '#f8fafc', border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`, borderRadius:10, padding:'12px 14px' }}>
            <input type="checkbox" style={{ width:18, height:18, accentColor:'#2563eb', cursor:'pointer' }} {...register('is_repeated')} />
            <label style={{ fontWeight:800, fontSize:13, cursor:'pointer' }}>هل الحدث متكرر؟</label>
          </div>
          <div>
            {label('رقم ملف المريض / المرجع')}
            <input type="text" placeholder="REF-2024-001" className="input-field" dir="ltr" {...register('reference')} />
          </div>
        </div>

        {/* Submit */}
        <button type="submit" disabled={isSubmitting} style={{
          background: isSubmitting ? '#94a3b8' : 'linear-gradient(135deg,#1f3b73,#2563eb)',
          border:'none', borderRadius:14, padding:'15px 0', color:'#fff',
          fontSize:15, fontWeight:900, fontFamily:"'Tajawal',sans-serif",
          cursor: isSubmitting ? 'not-allowed' : 'pointer',
          display:'flex', alignItems:'center', justifyContent:'center', gap:8,
          boxShadow: isSubmitting ? 'none' : '0 8px 20px rgba(37,99,235,0.35)',
          transition:'all 0.2s',
        }}>
          {isSubmitting ? '⏳ جارٍ الإرسال...' : '✈️ اعتماد وتسجيل الحدث'}
        </button>
      </form>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   SMALL COMPONENTS
══════════════════════════════════════════════════════════════ */
function HeaderBtn({ onClick, title, children }: { onClick: () => void; title: string; children: React.ReactNode }) {
  return (
    <button onClick={onClick} title={title} style={{
      position:'relative', width:38, height:38,
      background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.12)',
      borderRadius:10, cursor:'pointer',
      display:'flex', alignItems:'center', justifyContent:'center',
      color:'#cbd5e1', transition:'background 0.2s',
    }}>
      {children}
    </button>
  );
}

function Pagination({ page, total, onChange, darkMode }: { page: number; total: number; onChange: (p: number) => void; darkMode: boolean }) {
  if (total <= 1) return null;
  return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:10 }}>
      <button disabled={page <= 1} onClick={() => onChange(page - 1)} style={{ ...btnSecondary(darkMode), opacity: page <= 1 ? 0.4 : 1 }}>
        ＜ السابق
      </button>
      <span style={{ fontWeight:700, fontSize:13, color:'#94a3b8' }}>صفحة {page} من {total}</span>
      <button disabled={page >= total} onClick={() => onChange(page + 1)} style={{ ...btnSecondary(darkMode), opacity: page >= total ? 0.4 : 1 }}>
        التالي ＞
      </button>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:16 }}>
      {[1,2,3].map(i => (
        <div key={i} style={{ height:200, borderRadius:20, background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)', backgroundSize:'200% 100%', animation:'shimmer 1.5s ease-in-out infinite' }} />
      ))}
      <style>{`@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
    </div>
  );
}

function EmptyState({ onNew }: { onNew: () => void }) {
  return (
    <div style={{ background:'#fff', borderRadius:20, padding:'60px 40px', textAlign:'center', boxShadow:'0 10px 30px rgba(0,0,0,0.05)', border:'1px solid #e2e8f0' }}>
      <div style={{ fontSize:64, marginBottom:14 }}>📋</div>
      <div style={{ color:'#1f3b73', fontSize:18, fontWeight:900, marginBottom:8 }}>لا توجد بلاغات مسجلة</div>
      <div style={{ color:'#94a3b8', fontSize:13, fontWeight:600, marginBottom:22 }}>لم تقم بتسجيل أي أحداث بعد.</div>
      <button onClick={onNew} style={{ background:'linear-gradient(135deg,#1f3b73,#2563eb)', border:'none', borderRadius:12, padding:'12px 28px', color:'#fff', fontSize:14, fontWeight:900, fontFamily:"'Tajawal',sans-serif", cursor:'pointer', boxShadow:'0 6px 16px rgba(37,99,235,0.3)' }}>
        ⚡ سجّل حدثاً جديداً
      </button>
    </div>
  );
}

function ErrMsg({ msg }: { msg?: string }) {
  return <p style={{ color:'#ef4444', fontSize:11, fontWeight:700, marginTop:4 }}>{msg}</p>;
}

/* ── Style helpers ── */
function inputStyle(darkMode: boolean): React.CSSProperties {
  return {
    padding:'8px 12px', borderRadius:10,
    border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
    background: darkMode ? '#1e293b' : '#fff',
    color: darkMode ? '#f1f5f9' : '#1e293b',
    fontSize:13, fontWeight:700,
    fontFamily:"'Tajawal',sans-serif",
    outline:'none',
  };
}
function btnSecondary(darkMode: boolean): React.CSSProperties {
  return {
    background: darkMode ? '#1e293b' : '#fff',
    border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
    borderRadius:10, padding:'8px 16px',
    color: darkMode ? '#94a3b8' : '#64748b',
    fontSize:12, fontWeight:800, cursor:'pointer',
    fontFamily:"'Tajawal',sans-serif",
  };
}
function emptyBox(darkMode: boolean): React.CSSProperties {
  return {
    background: darkMode ? '#0f172a' : '#fff',
    borderRadius:20, padding:'60px 40px', textAlign:'center',
    boxShadow:'0 10px 30px rgba(0,0,0,0.05)',
    border:`2px dashed #e2e8f0`,
  };
}

/* ── Helpers ── */
function computeRiskLevel(score: number): string {
  if (score >= 20) return 'حرج';
  if (score >= 15) return 'مرتفع';
  if (score >= 8)  return 'عالي';
  if (score >= 4)  return 'متوسط';
  return 'منخفض';
}

/* ── WhatsApp SVG icon (shared) ── */
const waIcon = (
  <svg width="18" height="18" viewBox="0 0 32 32" fill="#25d366">
    <path d="M16 3C8.82 3 3 8.82 3 16c0 2.62.76 5.07 2.08 7.14L3 29l6.05-2.04A13 13 0 0016 29c7.18 0 13-5.82 13-13S23.18 3 16 3zm6.37 17.84c-.27.76-1.57 1.44-2.15 1.53-.56.09-1.27.13-2.05-.13-.47-.15-1.07-.36-1.84-.7-3.23-1.39-5.34-4.68-5.5-4.9-.16-.22-1.3-1.73-1.3-3.3 0-1.57.82-2.35 1.12-2.67.3-.32.65-.4.86-.4h.62c.2 0 .47-.07.73.56.27.65.92 2.24.99 2.4.08.16.13.36.03.57-.1.22-.16.35-.32.54l-.47.55c-.16.16-.33.33-.14.65.18.32.82 1.36 1.77 2.2 1.22 1.08 2.25 1.41 2.57 1.57.32.16.51.13.7-.08.18-.22.77-.9 1-.21.22.7.22 1.32-.05 1.62z"/>
  </svg>
);

/* ── WhatsApp message composers ── */
function shareIncidentWhatsApp(incident: IncidentRow) {
  const msg = [
    `📋 *بلاغ حدث تشغيلي — ${incident.event_id}*`,
    '━━━━━━━━━━━━━━━━━',
    `📍 *الموقع:* ${incident.location}`,
    `⚡ *النوع:* ${incident.event_type}`,
    `⚠️ *مستوى الخطورة:* ${incident.risk_level}`,
    `📊 *الحالة:* ${incident.status}`,
    `📝 *الوصف:*\n${incident.description}`,
    '━━━━━━━━━━━━━━━━━',
    '🏥 نظام مُناوِب',
  ].join('\n');
  window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
}

function shareUpdateWhatsApp(eventId: string, desc: string, actor: string) {
  const msg = [
    `🔄 *تحديث إجراء — ${eventId}*`,
    '━━━━━━━━━━━━━━━━━',
    `📝 ${desc}`,
    `👤 بواسطة: ${actor}`,
    `🕒 ${new Date().toLocaleTimeString('ar-SA', { hour:'2-digit', minute:'2-digit' })}`,
    '━━━━━━━━━━━━━━━━━',
    '🏥 نظام مُناوِب',
  ].join('\n');
  window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
}

/* ══════════════════════════════════════════════════════════════
   RCA VIEW MODAL
══════════════════════════════════════════════════════════════ */
function RcaViewModal({ incidentId, darkMode, onClose }: {
  incidentId: string; darkMode: boolean; onClose: () => void;
}) {
  const bg    = darkMode ? '#0f172a' : '#fff';
  const bgSub = darkMode ? '#1e293b' : '#f8fafc';

  const { data, isLoading } = useQuery({
    queryKey: ['rca_view', incidentId],
    queryFn: async () => {
      const [incRes, rcaRes] = await Promise.all([
        supabase.from('incidents').select('*').eq('id', incidentId).single(),
        supabase.from('rca_reports').select('*').eq('incident_id', incidentId).maybeSingle(),
      ]);
      return { incident: incRes.data, rca: rcaRes.data };
    },
  });

  const WHY_QS = [
    'ما الذي حدث؟ ولماذا حدث؟',
    'ما العوامل المساهمة التي أدت إلى ذلك؟',
    'لماذا لم يتم اكتشاف أو منع هذه العوامل؟',
    'ما الخلل النظامي أو التنظيمي الذي سمح بحدوث ذلك؟',
    'ما هو السبب الجذري النهائي؟',
  ];

  const handlePrint = () => {
    if (!data) return;
    const { incident: ev, rca } = data;
    const riskColors: Record<string,string> = { حرج:'#b91c1c', عالي:'#c2410c', مرتفع:'#c2410c', متوسط:'#b45309', منخفض:'#15803d' };
    const riskColor = riskColors[(ev as any)?.risk_level] || '#0f172a';
    const evDate    = ev ? new Date((ev as any).created_at).toLocaleDateString('ar-SA', { year:'numeric', month:'long', day:'numeric' }) : '—';
    const deadline  = rca?.deadline ? new Date(rca.deadline).toLocaleDateString('ar-SA', { year:'numeric', month:'long', day:'numeric' }) : '—';
    const printDate = new Date().toLocaleDateString('ar-SA', { year:'numeric', month:'long', day:'numeric', hour:'2-digit', minute:'2-digit' } as any);
    const whyVals   = rca ? [rca.why_1, rca.why_2, rca.why_3, rca.why_4, rca.root_cause] : [];

    const F = "font-family:'Tajawal',Arial,sans-serif;";
    const CSS = `
      @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700;900&display=swap');
      *{${F}box-sizing:border-box;margin:0;padding:0;}
      body{background:#f5f5f5;color:#333;font-size:13px;direction:rtl;}
      .wrap{max-width:800px;margin:0 auto;padding:24px 16px;}
      .card{background:#fff;border:1px solid #e2e8f0;border-radius:20px;overflow:hidden;box-shadow:0 10px 30px rgba(0,0,0,0.05);}
      .brand-hdr{background:linear-gradient(135deg,#0f172a,#1e1b4b,#2e1065);padding:22px 32px;text-align:center;}
      .brand-name{font-size:22px;font-weight:900;color:#fff;}
      .report-sub{font-size:12px;font-weight:700;color:#c4b5fd;margin-top:5px;}
      .body{padding:24px 32px;}
      .sec-hdr{font-size:13px;font-weight:900;color:#fff;padding:10px 16px;border-radius:12px 12px 0 0;}
      .purple{background:linear-gradient(90deg,#1e1b4b,#2e1065);}
      .green{background:linear-gradient(90deg,#065f46,#047857);}
      .mb20{margin-bottom:20px;}
      table.dt{width:100%;border-collapse:collapse;border:1px solid #e2e8f0;border-radius:0 0 12px 12px;overflow:hidden;}
      table.dt td{padding:11px 14px;font-size:13px;border-bottom:1px solid #e2e8f0;text-align:right;}
      table.dt td.lbl{font-weight:bold;color:#64748b;background:#f8fafc;width:33%;border-left:1px solid #e2e8f0;}
      table.dt td.val{font-weight:900;color:#0f172a;}
      table.dt tr:last-child td{border-bottom:none;}
      .desc-row{padding:12px 16px;background:#f8fafc;border:1px solid #e2e8f0;border-top:none;border-radius:0 0 12px 12px;}
      .why-box{border:1px solid #e2e8f0;border-top:none;border-radius:0 0 12px 12px;overflow:hidden;}
      .why-row{padding:12px 16px;border-bottom:1px solid #f1f5f9;}
      .why-row:last-child{border-bottom:none;}
      .why-q{font-size:11px;font-weight:700;color:#6d28d9;margin-bottom:4px;}
      .why-a{font-size:13px;font-weight:900;color:#0f172a;line-height:1.6;}
      .root .why-q{color:#b91c1c;} .root .why-a{color:#b91c1c;}
      .capa-box{border:1px solid #e2e8f0;border-top:none;border-radius:0 0 12px 12px;overflow:hidden;}
      .capa-action{padding:14px 16px;background:#f0fdf4;border-bottom:1px solid #d1fae5;}
      .footer{padding:14px 32px;background:#f8fafc;border-top:1px solid #e2e8f0;text-align:center;}
      .footer p{font-size:11px;color:#64748b;font-weight:bold;}
      *{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;}
      @media print{body{background:#fff;}.wrap{padding:0;max-width:100%;}@page{margin:1.5cm;size:A4;}}
    `;

    const H = `<!DOCTYPE html><html dir="rtl" lang="ar"><head><meta charset="UTF-8">
      <title>تقرير RCA — ${(ev as any)?.event_id ?? ''}</title>
      <style>${CSS}</style></head><body><div class="wrap"><div class="card">
      <div class="brand-hdr"><div class="brand-name">نظام مُناوِب | munawb</div>
      <div class="report-sub">تقرير تحليل السبب الجذري والخطة التصحيحية والوقائية (CAPA)</div></div>
      <div class="body">
        <div class="mb20">
          <div class="sec-hdr purple">معلومات الحدث</div>
          <table class="dt">
            <tr><td class="lbl">رقم الحدث</td><td class="val" dir="ltr" style="font-family:monospace;color:#1d4ed8;">${(ev as any)?.event_id ?? '—'}</td></tr>
            <tr><td class="lbl">القسم / الموقع</td><td class="val">${(ev as any)?.location ?? '—'}</td></tr>
            <tr><td class="lbl">مستوى الخطورة</td><td class="val" style="color:${riskColor}">${(ev as any)?.risk_level ?? '—'}</td></tr>
            <tr><td class="lbl">تاريخ الحدث</td><td class="val">${evDate}</td></tr>
          </table>
          ${(ev as any)?.description ? `<div class="desc-row"><div style="font-size:11px;font-weight:700;color:#64748b;margin-bottom:3px;">وصف الحدث</div><div style="font-size:13px;font-weight:700;line-height:1.6;">${(ev as any).description}</div></div>` : ''}
        </div>
        ${rca ? `
        <div class="mb20">
          <div class="sec-hdr purple">تحليل الأسباب الجذرية — منهجية الأسباب الخمسة</div>
          <div class="why-box">
            ${WHY_QS.map((q, i) => {
              const ans = (whyVals[i] ?? '').toString().trim();
              if (!ans) return '';
              return `<div class="why-row${i===4?' root':''}"><div class="why-q">س${i+1}: ${q}</div><div class="why-a">${ans}</div></div>`;
            }).join('')}
          </div>
        </div>
        <div class="mb20">
          <div class="sec-hdr green">خطة العمل التصحيحية والوقائية (CAPA Plan)</div>
          <div class="capa-box">
            ${rca.action_plan ? `<div class="capa-action"><div style="font-size:11px;font-weight:700;color:#065f46;margin-bottom:3px;">الإجراء التصحيحي</div><div style="font-size:13px;font-weight:900;line-height:1.6;">${rca.action_plan}</div></div>` : ''}
            <table class="dt" style="border-radius:0;">
              <tr><td class="lbl">المسؤول عن التنفيذ</td><td class="val">${rca.owner ?? '—'}</td></tr>
              <tr><td class="lbl">الموعد النهائي</td><td class="val">${deadline}</td></tr>
            </table>
          </div>
        </div>` : ''}
      </div>
      <div class="footer"><p>تقرير رسمي صادر من نظام مُناوِب • تاريخ الطباعة: ${printDate}</p></div>
    </div></div></body></html>`;

    const w = window.open('', '_blank');
    if (w) { w.document.write(H); w.document.close(); setTimeout(() => { w.focus(); w.print(); }, 500); }
  };

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:200, background:'rgba(15,23,42,0.92)', backdropFilter:'blur(12px)', display:'flex', justifyContent:'center', alignItems:'center', padding:16 }}>
      <div onClick={e => e.stopPropagation()} style={{
        background:bg, width:'100%', maxWidth:660, borderRadius:28,
        overflow:'hidden', display:'flex', flexDirection:'column',
        maxHeight:'90vh', animation:'modalIn 0.25s ease',
        border:'1px solid rgba(124,58,237,0.3)',
        boxShadow:'0 24px 64px rgba(0,0,0,0.5)',
      }}>
        {/* Header */}
        <div style={{ background:'linear-gradient(135deg,#2e1065,#4c1d95)', padding:'16px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
          <div style={{ color:'#fff', fontWeight:900, fontSize:15, display:'flex', alignItems:'center', gap:8 }}>
            📋 تقرير تحليل السبب الجذري
            {data?.incident && <span style={{ background:'rgba(255,255,255,0.15)', color:'#c4b5fd', fontFamily:'monospace', fontSize:11, padding:'2px 8px', borderRadius:6 }}>{(data.incident as any).event_id}</span>}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <button onClick={handlePrint} title="طباعة التقرير" style={{ background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:8, width:34, height:34, color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:15 }}>🖨️</button>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:8, width:34, height:34, color:'#fff', cursor:'pointer', fontSize:16 }}>✕</button>
          </div>
        </div>

        {isLoading ? (
          <div style={{ padding:60, textAlign:'center', color:'#94a3b8' }}>
            <div style={{ fontSize:32 }}>⏳</div>
            <div style={{ fontWeight:700, marginTop:8 }}>جارٍ تحميل التقرير...</div>
          </div>
        ) : (
          <div style={{ overflowY:'auto', flex:1, padding:20 }}>

            {/* Incident info */}
            <div style={{ background:'rgba(109,40,217,0.08)', border:'1px solid rgba(109,40,217,0.2)', borderRadius:14, padding:16, marginBottom:16 }}>
              <div style={{ color:'#7c3aed', fontSize:10, fontWeight:900, textTransform:'uppercase', letterSpacing:2, marginBottom:10 }}>معلومات الحدث</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
                {[
                  { l:'رقم الحدث', v:(data?.incident as any)?.event_id ?? '—' },
                  { l:'مستوى الخطورة', v:(data?.incident as any)?.risk_level ?? '—' },
                  { l:'القسم / الموقع', v:(data?.incident as any)?.location ?? '—' },
                  { l:'تاريخ الحدث', v:(data?.incident as any)?.created_at ? new Date((data?.incident as any).created_at).toLocaleDateString('ar-SA') : '—' },
                ].map(d => (
                  <div key={d.l}>
                    <div style={{ color:'#94a3b8', fontSize:10, fontWeight:700, marginBottom:3 }}>{d.l}</div>
                    <div style={{ fontWeight:900, fontSize:13 }}>{d.v}</div>
                  </div>
                ))}
              </div>
              {(data?.incident as any)?.description && (
                <div>
                  <div style={{ color:'#94a3b8', fontSize:10, fontWeight:700, marginBottom:4 }}>وصف الحدث</div>
                  <p style={{ fontWeight:700, fontSize:13, lineHeight:1.6, margin:0 }}>{(data?.incident as any)?.description}</p>
                </div>
              )}
            </div>

            {/* RCA section */}
            {!data?.rca ? (
              <div style={{ background:'#fffbeb', border:'1px solid #fde68a', borderRadius:14, padding:20, textAlign:'center' }}>
                <div style={{ fontSize:32, marginBottom:8 }}>⚠️</div>
                <div style={{ color:'#92400e', fontWeight:800, fontSize:13 }}>لم يتم العثور على تحليل RCA مسجّل لهذا الحدث.</div>
              </div>
            ) : (
              <>
                {/* 5 Whys */}
                <div style={{ background: darkMode ? '#1e293b' : '#fff', border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`, borderRadius:14, overflow:'hidden', marginBottom:14 }}>
                  <div style={{ background:'linear-gradient(90deg,#1e1b4b,#2e1065)', padding:'10px 16px' }}>
                    <span style={{ color:'#fff', fontWeight:900, fontSize:13 }}>🔬 تحليل الأسباب الجذرية — أسلوب الأسباب الخمسة</span>
                  </div>
                  {WHY_QS.map((q, i) => {
                    const vals = data.rca ? [data.rca.why_1, data.rca.why_2, data.rca.why_3, data.rca.why_4, data.rca.root_cause] : [];
                    const ans  = vals[i];
                    if (!ans) return null;
                    const isRoot = i === 4;
                    return (
                      <div key={i} style={{ padding:'12px 16px', borderBottom: i < 4 ? `1px solid ${darkMode ? '#334155' : '#f1f5f9'}` : 'none' }}>
                        <div style={{ color: isRoot ? '#b91c1c' : '#7c3aed', fontSize:10, fontWeight:800, marginBottom:4 }}>س{i+1}: {q}</div>
                        <div style={{ color: isRoot ? '#b91c1c' : (darkMode ? '#e2e8f0' : '#1e293b'), fontWeight:900, fontSize:13, lineHeight:1.6 }}>{ans}</div>
                      </div>
                    );
                  })}
                </div>

                {/* CAPA Plan */}
                <div style={{ background: darkMode ? '#1e293b' : '#fff', border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`, borderRadius:14, overflow:'hidden' }}>
                  <div style={{ background:'linear-gradient(90deg,#065f46,#047857)', padding:'10px 16px' }}>
                    <span style={{ color:'#fff', fontWeight:900, fontSize:13 }}>📋 خطة العمل التصحيحية والوقائية (CAPA Plan)</span>
                  </div>
                  <div style={{ padding:16 }}>
                    {data.rca.action_plan && (
                      <div style={{ background: darkMode ? '#0f172a' : '#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10, padding:12, marginBottom:12 }}>
                        <div style={{ color:'#065f46', fontSize:10, fontWeight:800, marginBottom:4 }}>الإجراء التصحيحي</div>
                        <p style={{ fontWeight:800, fontSize:13, lineHeight:1.6, margin:0 }}>{data.rca.action_plan}</p>
                      </div>
                    )}
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                      <div>
                        <div style={{ color:'#94a3b8', fontSize:10, fontWeight:700, marginBottom:3 }}>المسؤول عن التنفيذ</div>
                        <div style={{ fontWeight:900, fontSize:13 }}>{data.rca.owner ?? '—'}</div>
                      </div>
                      <div>
                        <div style={{ color:'#94a3b8', fontSize:10, fontWeight:700, marginBottom:3 }}>الموعد النهائي</div>
                        <div style={{ fontWeight:900, fontSize:13 }}>{data.rca.deadline ? new Date(data.rca.deadline).toLocaleDateString('ar-SA') : '—'}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   PROFILE MODAL
══════════════════════════════════════════════════════════════ */
function ProfileModal({ profile, darkMode, onClose, onSuccess, onError }: {
  profile: any; darkMode: boolean;
  onClose: () => void; onSuccess: (msg: string) => void; onError: (msg: string) => void;
}) {
  const bg    = darkMode ? '#0f172a' : '#fff';
  const bgSub = darkMode ? '#1e293b' : '#f8fafc';

  const [phone, setPhone]         = useState<string>((profile as any)?.phone ?? '');
  const [oldPass, setOldPass]     = useState('');
  const [newPass, setNewPass]     = useState('');
  const [confPass, setConfPass]   = useState('');
  const [showOld, setShowOld]     = useState(false);
  const [showNew, setShowNew]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [notifEmail, setNotifEmail] = useState(true);
  const [notifWa, setNotifWa]       = useState(true);
  const passError = newPass && confPass && newPass !== confPass;

  const handleSave = async () => {
    if (newPass && !oldPass) { onError('يجب إدخال كلمة المرور الحالية أولاً.'); return; }
    if (passError) { onError('كلمتا المرور الجديدتان غير متطابقتين!'); return; }
    setSaving(true);
    try {
      // Update phone in profiles table
      const { error: profileErr } = await supabase
        .from('profiles')
        .update({ phone, notif_email: notifEmail, notif_wa: notifWa } as any)
        .eq('id', profile?.id);
      if (profileErr) throw profileErr;

      // Update password if provided
      if (newPass) {
        const { error: passErr } = await supabase.auth.updateUser({ password: newPass });
        if (passErr) throw passErr;
      }
      onSuccess('تم حفظ تحديثات الملف الشخصي بنجاح ✅');
    } catch (e: any) {
      onError(e?.message ?? 'حدث خطأ أثناء الحفظ');
    } finally { setSaving(false); }
  };

  const inputSt: React.CSSProperties = {
    width:'100%', padding:'10px 12px', borderRadius:10,
    border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
    background: darkMode ? '#0f172a' : '#fff',
    color: darkMode ? '#f1f5f9' : '#1e293b',
    fontSize:13, fontWeight:700,
    fontFamily:"'Tajawal',sans-serif", outline:'none', boxSizing:'border-box',
  };
  const disabledSt: React.CSSProperties = { ...inputSt, background: darkMode ? '#1e293b' : '#f1f5f9', color:'#94a3b8', cursor:'not-allowed' };
  const lbl = (t: string) => <div style={{ color:'#64748b', fontSize:11, fontWeight:800, marginBottom:5 }}>{t}</div>;

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:70, background:'rgba(15,23,42,0.65)', backdropFilter:'blur(6px)', display:'flex', justifyContent:'center', alignItems:'center', padding:16 }}>
      <div onClick={e => e.stopPropagation()} style={{
        background:bg, width:'100%', maxWidth:520, borderRadius:24,
        overflow:'hidden', display:'flex', flexDirection:'column',
        maxHeight:'90vh', animation:'modalIn 0.25s ease',
        border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
        boxShadow:'0 24px 64px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{ background:'linear-gradient(135deg,#1d4ed8,#4f46e5)', padding:'16px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
          <div style={{ color:'#fff', fontWeight:900, fontSize:15 }}>⚙️ إعدادات الملف الشخصي</div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.7)', fontSize:20, cursor:'pointer' }}>✕</button>
        </div>

        <div style={{ overflowY:'auto', flex:1, padding:20 }}>

          {/* User info (read-only) */}
          <div style={{ background:bgSub, borderRadius:14, padding:16, marginBottom:16, border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}` }}>
            <div style={{ color:'#3b82f6', fontSize:11, fontWeight:900, marginBottom:12, textTransform:'uppercase', letterSpacing:1 }}>👤 بيانات المستخدم</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
              <div>{lbl('الاسم الكامل')}<input readOnly value={profile?.name ?? ''} style={disabledSt} /></div>
              <div>{lbl('الإدارة / القسم')}<input readOnly value={(profile as any)?.department?.name ?? ''} style={disabledSt} /></div>
            </div>
            <div style={{ marginBottom:12 }}>
              {lbl('البريد الإلكتروني')}
              <input readOnly value={profile?.email ?? ''} dir="ltr" style={disabledSt} />
            </div>
            <div>
              {lbl('رقم الجوال')}
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <span style={{ color:'#94a3b8', fontSize:13, fontWeight:700 }}>+966</span>
                <input
                  type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                  placeholder="5xxxxxxxx" dir="ltr"
                  style={{ ...inputSt, flex:1 }}
                />
              </div>
            </div>
          </div>

          {/* Password */}
          <div style={{ background:bgSub, borderRadius:14, padding:16, marginBottom:16, border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}` }}>
            <div style={{ color:'#3b82f6', fontSize:11, fontWeight:900, marginBottom:12, textTransform:'uppercase', letterSpacing:1 }}>🔒 إعدادات الأمان</div>
            <p style={{ color:'#94a3b8', fontSize:10, fontWeight:700, marginBottom:12 }}>اترك حقول كلمة المرور فارغة إذا لم ترغب في تغييرها.</p>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              <div>
                {lbl('كلمة المرور الحالية')}
                <div style={{ position:'relative' }}>
                  <input type={showOld ? 'text' : 'password'} value={oldPass} onChange={e => setOldPass(e.target.value)} placeholder="••••••••" style={{ ...inputSt, paddingLeft:36 }} />
                  <button onClick={() => setShowOld(v => !v)} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#94a3b8', fontSize:14 }}>
                    {showOld ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div>
                  {lbl('كلمة المرور الجديدة')}
                  <div style={{ position:'relative' }}>
                    <input type={showNew ? 'text' : 'password'} value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="••••••••" style={{ ...inputSt, paddingLeft:36 }} />
                    <button onClick={() => setShowNew(v => !v)} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#94a3b8', fontSize:14 }}>
                      {showNew ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>
                <div>
                  {lbl('تأكيد كلمة المرور')}
                  <input type="password" value={confPass} onChange={e => setConfPass(e.target.value)} placeholder="••••••••" style={{ ...inputSt, border: passError ? '1px solid #ef4444' : inputSt.border }} />
                </div>
              </div>
              {passError && <p style={{ color:'#ef4444', fontSize:11, fontWeight:700 }}>⚠️ كلمتا المرور الجديدتان غير متطابقتين!</p>}
            </div>
          </div>

          {/* Notification prefs */}
          <div style={{ background:bgSub, borderRadius:14, padding:16, marginBottom:20, border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}` }}>
            <div style={{ color:'#3b82f6', fontSize:11, fontWeight:900, marginBottom:12, textTransform:'uppercase', letterSpacing:1 }}>🔔 تفضيلات الإشعارات</div>
            {[
              { label:'إشعارات البريد الإلكتروني', sub:'استلام تحديثات البلاغات على البريد', val:notifEmail, set:setNotifEmail },
              { label:'إشعارات واتساب (WhatsApp)', sub:'التنبيهات العاجلة للأحداث المصعدة', val:notifWa, set:setNotifWa },
            ].map(item => (
              <div key={item.label} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingBottom:12, marginBottom:12, borderBottom:`1px solid ${darkMode ? '#334155' : '#f1f5f9'}` }}>
                <div>
                  <div style={{ fontWeight:800, fontSize:13 }}>{item.label}</div>
                  <div style={{ color:'#94a3b8', fontSize:10, fontWeight:600, marginTop:2 }}>{item.sub}</div>
                </div>
                <button
                  onClick={() => item.set(v => !v)}
                  style={{
                    width:44, height:24, borderRadius:12, border:'none', cursor:'pointer',
                    background: item.val ? '#3b82f6' : '#cbd5e1',
                    position:'relative', transition:'background 0.2s', flexShrink:0,
                  }}
                >
                  <span style={{
                    position:'absolute', top:2, width:20, height:20, background:'#fff', borderRadius:'50%',
                    transition:'left 0.2s', left: item.val ? 22 : 2,
                    boxShadow:'0 1px 4px rgba(0,0,0,0.2)',
                  }} />
                </button>
              </div>
            ))}
          </div>

          {/* Buttons */}
          <div style={{ display:'flex', gap:10 }}>
            <button onClick={onClose} style={{ flex:1, background: darkMode ? '#334155' : '#f1f5f9', border:'none', borderRadius:10, padding:'12px', color: darkMode ? '#94a3b8' : '#64748b', fontWeight:900, fontSize:13, cursor:'pointer', fontFamily:"'Tajawal',sans-serif" }}>
              إلغاء
            </button>
            <button onClick={handleSave} disabled={saving || !!passError} style={{
              flex:2, background: saving || passError ? '#94a3b8' : '#2563eb',
              border:'none', borderRadius:10, padding:'12px', color:'#fff',
              fontWeight:900, fontSize:13, cursor: saving || passError ? 'not-allowed' : 'pointer',
              fontFamily:"'Tajawal',sans-serif", display:'flex', alignItems:'center', justifyContent:'center', gap:8,
            }}>
              {saving ? '⏳ جارٍ الحفظ...' : '💾 حفظ التحديثات'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   CALL MODAL — نظام الاستدعاء الطارئ
══════════════════════════════════════════════════════════════ */
const QUICK_CODES = [
  { code:'CODE_BLUE',    label:'Code Blue',       icon:'💙', color:'#1d4ed8', bg:'#eff6ff', desc:'استدعاء فريق الإنعاش القلبي الرئوي',    priority:'حرج جداً (فوراً)' },
  { code:'CODE_RED',     label:'Code Red',        icon:'🔴', color:'#dc2626', bg:'#fef2f2', desc:'حريق أو دخان — استدعاء فريق السلامة',   priority:'حرج جداً (فوراً)' },
  { code:'CODE_PINK',    label:'Code Pink',       icon:'🩷', color:'#db2777', bg:'#fdf2f8', desc:'اختطاف طفل — إغلاق المخارج فوراً',      priority:'حرج جداً (فوراً)' },
  { code:'CODE_YELLOW',  label:'Code Yellow',     icon:'🟡', color:'#d97706', bg:'#fffbeb', desc:'كارثة داخلية أو ارتفاع مفاجئ في الأحمال', priority:'عاجل (خلال 15 دقيقة)' },
  { code:'CODE_ORANGE',  label:'Code Orange',     icon:'🟠', color:'#ea580c', bg:'#fff7ed', desc:'كارثة خارجية MCI — تفعيل خطة الكوارث', priority:'حرج جداً (فوراً)' },
  { code:'CODE_BLACK',   label:'Code Black',      icon:'⚫', color:'#334155', bg:'#f8fafc', desc:'تهديد أمني أو إخلاء طارئ للمبنى',       priority:'حرج جداً (فوراً)' },
  { code:'CODE_CONSULT', label:'استدعاء استشاري', icon:'🟢', color:'#059669', bg:'#f0fdf4', desc:'طلب حضور الاستشاري المناوب',             priority:'عاجل (خلال 15 دقيقة)' },
  { code:'CODE_CUSTOM',  label:'مخصص',            icon:'✏️', color:'#6d28d9', bg:'#f5f3ff', desc:'',                                         priority:'' },
];
const PRIORITIES = ['حرج جداً (فوراً)', 'عاجل (خلال 15 دقيقة)', 'طارئ (خلال ساعة)'];

function CallModal({ profile, departments, darkMode, onClose, onSuccess, onError }: {
  profile: any; departments: { id:string; name:string }[]; darkMode: boolean;
  onClose: () => void; onSuccess: (msg: string) => void; onError: (msg: string) => void;
}) {
  const bg    = darkMode ? '#0f172a' : '#fff';
  const bgSub = darkMode ? '#1e293b' : '#f8fafc';

  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [callType, setCallType]         = useState('');
  const [priority, setPriority]         = useState('');
  const [location, setLocation]         = useState((profile as any)?.department?.name ?? '');
  const [details, setDetails]           = useState('');
  const [notifyWa, setNotifyWa]         = useState(true);
  const [notifyExec, setNotifyExec]     = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [callLog, setCallLog]           = useState<{ label:string; loc:string; time:string }[]>([]);
  const [timerSecs, setTimerSecs]       = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);

  // Timer
  useEffect(() => {
    if (!timerRunning) return;
    const iv = setInterval(() => setTimerSecs(s => s + 1), 1000);
    return () => clearInterval(iv);
  }, [timerRunning]);

  const fmtTimer = (s: number) => `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;

  const selectCode = (code: typeof QUICK_CODES[0]) => {
    setSelectedCode(code.code);
    if (code.code !== 'CODE_CUSTOM') {
      setCallType(code.label);
      setPriority(code.priority);
      setDetails(code.desc);
    } else {
      setCallType('');
      setPriority('');
      setDetails('');
    }
  };

  const handleSubmit = async () => {
    if (!callType) { onError('يرجى اختيار نوع الاستدعاء أو الجهة المطلوبة.'); return; }
    if (!priority) { onError('يرجى تحديد مستوى الأهمية.'); return; }
    if (!location) { onError('يرجى تحديد الموقع بدقة.'); return; }
    setSubmitting(true);
    try {
      await supabase.from('incidents').insert({
        event_type:   `[استدعاء] ${callType}`,
        location,
        description:  `${priority} — ${details || callType}\n📢 المُبلِّغ: ${profile?.name ?? ''}${notifyExec ? '\n🔺 تصعيد للإدارة' : ''}`,
        risk_level:   priority === 'حرج جداً (فوراً)' ? 'حرج' : priority === 'عاجل (خلال 15 دقيقة)' ? 'عالي' : 'متوسط',
        risk_score:   priority === 'حرج جداً (فوراً)' ? 25 : priority === 'عاجل (خلال 15 دقيقة)' ? 12 : 6,
        reporter_id:  profile?.id ?? null,
        reporter_name: profile?.name ?? '',
        is_repeated:  false,
        likelihood:   5,
        impact:       5,
      });

      if (notifyWa) {
        const emoji = QUICK_CODES.find(c => c.code === selectedCode)?.icon ?? '🚨';
        const msg = [
          `${emoji} *${callType} — استدعاء طارئ* ${emoji}`,
          '━━━━━━━━━━━━━━━━━━━━',
          `📍 *الموقع:* ${location}`,
          `⚡ *مستوى الأهمية:* ${priority}`,
          details ? `📋 *التفاصيل:* ${details}` : '',
          '━━━━━━━━━━━━━━━━━━━━',
          `👤 *المُبلِّغ:* ${profile?.name ?? ''}`,
          `🕒 ${new Date().toLocaleTimeString('ar-SA', { hour:'2-digit', minute:'2-digit' })}`,
        ].filter(Boolean).join('\n');
        window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
      }

      setTimerRunning(true);
      setCallLog(prev => [{ label: callType, loc: location, time: new Date().toLocaleTimeString('ar-SA', { hour:'2-digit', minute:'2-digit' }) }, ...prev]);
      setCallType(''); setPriority(''); setDetails(''); setSelectedCode(null);
      onSuccess('تم إطلاق نداء الاستدعاء بنجاح! 🚨');
    } catch (e: any) {
      onError(e?.message ?? 'تعذّر إرسال الاستدعاء');
    } finally { setSubmitting(false); }
  };

  const selectSt: React.CSSProperties = {
    width:'100%', padding:'10px 12px', borderRadius:10,
    border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
    background: darkMode ? '#0f172a' : '#fff',
    color: darkMode ? '#f1f5f9' : '#1e293b',
    fontSize:13, fontWeight:700, fontFamily:"'Tajawal',sans-serif", outline:'none',
  };
  const textareaSt: React.CSSProperties = { ...selectSt, resize:'none' };
  const lbl = (t: string) => <div style={{ color:'#64748b', fontSize:11, fontWeight:800, marginBottom:5 }}>{t}</div>;

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:80, background:'rgba(15,23,42,0.88)', backdropFilter:'blur(10px)', display:'flex', justifyContent:'center', alignItems:'center', padding:16 }}>
      <div onClick={e => e.stopPropagation()} style={{
        background:bg, width:'100%', maxWidth:600, borderRadius:28,
        overflow:'hidden', display:'flex', flexDirection:'column',
        maxHeight:'94vh', animation:'modalIn 0.25s ease',
        border:'1px solid rgba(239,68,68,0.2)',
        boxShadow:'0 24px 64px rgba(0,0,0,0.5)',
      }}>
        {/* Header */}
        <div style={{ background:'linear-gradient(135deg,#1a0505,#3b0a0a,#1e1b4b)', padding:'16px 20px', flexShrink:0 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <div style={{ width:42, height:42, background:'rgba(239,68,68,0.3)', border:'1px solid rgba(239,68,68,0.4)', borderRadius:12, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, animation:'emergencyPulse 1.6s ease-in-out infinite' }}>
                📢
              </div>
              <div>
                <div style={{ color:'#fff', fontWeight:900, fontSize:15, display:'flex', alignItems:'center', gap:8 }}>
                  نظام الاستدعاء والإعلان الطارئ
                  <span style={{ background:'rgba(239,68,68,0.4)', border:'1px solid rgba(239,68,68,0.5)', color:'#fca5a5', fontSize:9, fontWeight:900, padding:'2px 8px', borderRadius:20, textTransform:'uppercase', letterSpacing:2 }}>LIVE</span>
                </div>
                <div style={{ color:'rgba(252,165,165,0.8)', fontSize:11, fontWeight:700, marginTop:2 }}>تنبيه الفرق وتنسيق الاستجابة الطارئة</div>
              </div>
            </div>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.2)', borderRadius:8, width:34, height:34, color:'#fff', cursor:'pointer', fontSize:16 }}>✕</button>
          </div>
        </div>

        {/* Timer bar */}
        {timerRunning && (
          <div style={{ background:'#dc2626', padding:'8px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, color:'#fff', fontWeight:900, fontSize:13 }}>
              ⏱ وقت الاستدعاء: <span style={{ fontFamily:'monospace', fontSize:16 }}>{fmtTimer(timerSecs)}</span>
            </div>
            <div style={{ width:140, height:4, background:'rgba(255,255,255,0.2)', borderRadius:2, overflow:'hidden' }}>
              <div style={{ width:`${Math.min(100, (timerSecs/1800)*100)}%`, height:'100%', background:'linear-gradient(90deg,#22c55e,#f59e0b,#ef4444)', transition:'width 1s linear' }} />
            </div>
          </div>
        )}

        <div style={{ overflowY:'auto', flex:1, padding:20 }}>

          {/* Quick codes grid */}
          <div style={{ marginBottom:20 }}>
            <div style={{ color:'#94a3b8', fontSize:10, fontWeight:900, textTransform:'uppercase', letterSpacing:2, marginBottom:12, display:'flex', alignItems:'center', gap:6 }}>
              ⚡ تفعيل سريع — أكواد الطوارئ المعتمدة
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
              {QUICK_CODES.map(code => (
                <button
                  key={code.code}
                  onClick={() => selectCode(code)}
                  style={{
                    padding:'10px 6px', borderRadius:10, border:`2px solid ${selectedCode === code.code ? code.color : 'transparent'}`,
                    background: selectedCode === code.code ? code.bg : (darkMode ? '#1e293b' : '#f8fafc'),
                    color: code.color, cursor:'pointer', fontFamily:"'Tajawal',sans-serif",
                    fontWeight:900, fontSize:11, display:'flex', flexDirection:'column', alignItems:'center', gap:4,
                    transform: selectedCode === code.code ? 'scale(1.05)' : 'scale(1)',
                    transition:'all 0.2s', boxShadow: selectedCode === code.code ? `0 0 0 3px rgba(0,0,0,0.1)` : 'none',
                  }}
                >
                  <span style={{ fontSize:18 }}>{code.icon}</span>
                  {code.label}
                </button>
              ))}
            </div>
          </div>

          {/* Form */}
          <div style={{ display:'flex', flexDirection:'column', gap:14, borderTop:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`, paddingTop:16 }}>

            {/* Call type + priority */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                {lbl('نوع الاستدعاء / الجهة المطلوبة *')}
                <input
                  value={callType} onChange={e => setCallType(e.target.value)}
                  placeholder="مثال: Code Blue، فريق الإنعاش..."
                  style={{ ...selectSt }}
                />
              </div>
              <div>
                {lbl('مستوى الأهمية *')}
                <select value={priority} onChange={e => setPriority(e.target.value)} style={selectSt}>
                  <option value="">حدد مستوى الأهمية...</option>
                  {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>

            {/* Location */}
            <div>
              {lbl('الموقع بدقة (القسم / الغرفة) *')}
              <select value={location} onChange={e => setLocation(e.target.value)} style={selectSt}>
                <option value="">اختر الموقع...</option>
                {departments.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
              </select>
            </div>

            {/* Details */}
            <div>
              {lbl('تفاصيل الاستدعاء (اختياري)')}
              <textarea value={details} onChange={e => setDetails(e.target.value)} rows={2}
                placeholder="اكتب أي معلومات إضافية تفيد الفريق المستدعى..."
                style={textareaSt}
              />
            </div>

            {/* Channels */}
            <div style={{ background:bgSub, borderRadius:12, padding:14, border:`1px solid ${darkMode ? '#334155' : '#fee2e2'}` }}>
              <div style={{ color:'#dc2626', fontSize:10, fontWeight:900, textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>📡 قنوات النشر الإضافية</div>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                {[
                  { label:'إشعار داخلي (مُناوِب)', icon:'🔔', val:true,       set:()=>{},       color:'#2563eb', disabled:true },
                  { label:'واتساب (قناة الطوارئ)', icon:'💬', val:notifyWa,   set:setNotifyWa,  color:'#25d366', disabled:false },
                  { label:'تصعيد للإدارة',          icon:'👔', val:notifyExec, set:setNotifyExec,color:'#dc2626', disabled:false },
                ].map(ch => (
                  <label key={ch.label} style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px', borderRadius:8, border:`1px solid ${ch.val ? ch.color : (darkMode ? '#334155' : '#e2e8f0')}`, background: ch.val ? `${ch.color}15` : 'transparent', cursor: ch.disabled ? 'not-allowed' : 'pointer' }}>
                    <input type="checkbox" checked={ch.val} onChange={() => !ch.disabled && ch.set((v: boolean) => !v)} style={{ accentColor:ch.color, cursor: ch.disabled ? 'not-allowed' : 'pointer' }} />
                    <span style={{ fontSize:11, fontWeight:800, color: ch.val ? ch.color : '#94a3b8' }}>{ch.icon} {ch.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Buttons */}
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={onClose} style={{ flex:1, background: darkMode ? '#334155' : '#f1f5f9', border:'none', borderRadius:10, padding:'12px', color: darkMode ? '#94a3b8' : '#64748b', fontWeight:900, fontSize:13, cursor:'pointer', fontFamily:"'Tajawal',sans-serif" }}>
                إلغاء
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  flex:2, background: submitting ? '#94a3b8' : '#dc2626',
                  border:'none', borderRadius:10, padding:'12px', color:'#fff',
                  fontWeight:900, fontSize:14, cursor: submitting ? 'not-allowed' : 'pointer',
                  fontFamily:"'Tajawal',sans-serif",
                  display:'flex', alignItems:'center', justifyContent:'center', gap:8,
                  boxShadow: submitting ? 'none' : '0 0 0 0 rgba(239,68,68,0.4)',
                  animation: submitting ? 'none' : 'emergencyPulse 1.6s ease-in-out infinite',
                }}
              >
                {submitting ? '⏳ جارٍ الإرسال...' : '📢 إطلاق نداء الاستدعاء فوراً'}
              </button>
            </div>
          </div>

          {/* Session log */}
          {callLog.length > 0 && (
            <div style={{ borderTop:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`, paddingTop:16, marginTop:16 }}>
              <div style={{ color:'#94a3b8', fontSize:10, fontWeight:900, textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>📜 سجل الاستدعاءات (هذه الجلسة)</div>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {callLog.map((log, i) => (
                  <div key={i} style={{ background: darkMode ? '#1e293b' : '#f8fafc', borderRadius:8, padding:'8px 12px', display:'flex', justifyContent:'space-between', alignItems:'center', border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}` }}>
                    <span style={{ fontWeight:800, fontSize:12 }}>🚨 {log.label} — {log.loc}</span>
                    <span style={{ color:'#94a3b8', fontSize:10, fontWeight:600 }}>{log.time}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   LOGOUT MODAL
══════════════════════════════════════════════════════════════ */
function LogoutModal({ darkMode, onConfirm, onCancel }: {
  darkMode: boolean; onConfirm: () => void; onCancel: () => void;
}) {
  const bg = darkMode ? '#1e293b' : '#fff';
  return (
    <div onClick={onCancel} style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(15,23,42,0.7)', backdropFilter:'blur(4px)', display:'flex', justifyContent:'center', alignItems:'center', padding:16 }}>
      <div onClick={e => e.stopPropagation()} style={{
        background:bg, borderRadius:20, padding:'28px 24px', width:'100%', maxWidth:360,
        textAlign:'center', boxShadow:'0 24px 64px rgba(0,0,0,0.35)',
        border:`1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
        animation:'modalIn 0.2s ease',
      }}>
        <div style={{ width:56, height:56, background:'rgba(239,68,68,0.12)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px', fontSize:26 }}>
          🔴
        </div>
        <div style={{ fontWeight:900, fontSize:18, marginBottom:8 }}>تسجيل الخروج</div>
        <p style={{ color:'#94a3b8', fontSize:13, fontWeight:600, marginBottom:24, lineHeight:1.6 }}>
          هل أنت متأكد من رغبتك في الخروج من نظام مُناوِب؟
        </p>
        <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
          <button onClick={onCancel} style={{ background: darkMode ? '#334155' : '#f1f5f9', border:'none', borderRadius:10, padding:'10px 24px', color: darkMode ? '#94a3b8' : '#64748b', fontWeight:900, fontSize:13, cursor:'pointer', fontFamily:"'Tajawal',sans-serif" }}>
            إلغاء
          </button>
          <button onClick={onConfirm} style={{ background:'#dc2626', border:'none', borderRadius:10, padding:'10px 24px', color:'#fff', fontWeight:900, fontSize:13, cursor:'pointer', fontFamily:"'Tajawal',sans-serif", boxShadow:'0 4px 12px rgba(220,38,38,0.3)' }}>
            🚪 نعم، خروج
          </button>
        </div>
      </div>
    </div>
  );
}
